import {
  Attendance,
  AttendanceCompletion,
  AttendanceStatus,
  Event,
  Shift,
  Signup,
  Source,
} from '@amzn/red-velvet-api';
import { asTimeString, formatShiftDate } from './dateFormatting';
import { LoginData } from '../hooks/login/login';

export function generateShiftStartDateInfo(
  shift: Shift,
  selectedTimezone: string,
  language: string,
  defaultDisplayValue: string
) {
  const startDate = shift.startDateTime ? new Date(shift.startDateTime) : null;
  // Short, locale-aware date ("Jan 21, 2026") — the same formatter the volunteer table uses, so
  // coordinator and volunteer surfaces render dates identically. A literal short string would drop
  // the resolved-locale support, so route through formatShiftDate rather than hardcoding a format.
  // Start-only fallback mirrors generateShiftStartAndEndTimeInfo (and the volunteer table): a shift
  // with a start but no end shows the start date rather than dropping it to the default display value.
  if (startDate) return formatShiftDate(startDate, selectedTimezone, language);
  return defaultDisplayValue;
}

/**
 * Formats a shift's full "[start] - [end]" time range, mirroring the volunteer TimeCell. Both ends use
 * the same asTimeString call, so the timezone and locale handling matches the volunteer surface exactly.
 */
export function generateShiftStartAndEndTimeInfo(
  shift: Shift,
  selectedTimezone: string,
  language: string,
  defaultDisplayValue: string
) {
  const startDateTime = shift.startDateTime ? new Date(shift.startDateTime) : null;
  const endDateTime = shift.endDateTime ? new Date(shift.endDateTime) : null;
  const start = startDateTime
    ? asTimeString(startDateTime, selectedTimezone, language, { removeTimeZone: true })
    : null;
  const end = endDateTime
    ? asTimeString(endDateTime, selectedTimezone, language, { removeTimeZone: true })
    : null;
  if (start && end) return `${start} - ${end}`;
  // Start-only fallback mirrors the volunteer TimeCell: a shift with a start but no end shows the
  // start time rather than dropping it to the default display value.
  if (start) return start;
  return defaultDisplayValue;
}

export function generateShiftNumberOfSignedUpInfo(shift: Shift, signups: Signup[] | undefined) {
  const numberOfGuests = shift.totalNumberOfGuests ?? 0;
  const signupCount = signups?.length ?? 0;
  return { signupCount, numberOfGuests };
}

export function generateShiftNumberOfPeopleAttended(attendances: Attendance[] | undefined) {
  return attendances
    ? `${attendances.filter((attendance) => attendance.attendanceStatus === AttendanceStatus.ATTENDED).length}`
    : '0';
}

type actionLinkType = 'signup';

/**
 * The coordinator's Track attendance destination for a shift — always the track-attendance flow.
 * Kept separate from generateShiftActionLink so callers that unconditionally want Track attendance
 * (e.g. the in-progress kebab) don't accidentally route to the sign-up view-event page when
 * attendanceCompletion is undefined/UPCOMING.
 */
export function generateCoordinatorTrackAttendanceUrl(
  eventId: string,
  shiftId: string,
  urlFeatures: string | null
) {
  return `/track-attendance?page=1&eventId=${eventId}&shiftId=${shiftId}${urlFeatures ? '&features=' + urlFeatures : ''}`;
}

export function generateShiftActionLink(
  attendanceCompletion: string | undefined,
  shiftId: string,
  eventId: string,
  urlFeatures: string | null,
  action: actionLinkType = 'signup'
) {
  const eventDetailUrl = `/view-event?id=${eventId}&coordinator-action=${action}${urlFeatures ? '&features=' + urlFeatures : ''}`;
  const coordinatorTrackAttendanceUrl = generateCoordinatorTrackAttendanceUrl(
    eventId,
    shiftId,
    urlFeatures
  );
  if (!attendanceCompletion || AttendanceCompletion.UPCOMING === attendanceCompletion) {
    return eventDetailUrl;
  } else {
    return coordinatorTrackAttendanceUrl;
  }
}

/**
 * A shift is in progress once it has started but has neither ended nor been flagged complete.
 * Timestamps are Date | string (SDK-parsed), so normalise both before comparing.
 */
export function isShiftInProgress(shift: Shift, now: number = Date.now()): boolean {
  if (shift.complete === true) return false;
  const start = shift.startDateTime ? new Date(shift.startDateTime).getTime() : undefined;
  const end = shift.endDateTime ? new Date(shift.endDateTime).getTime() : undefined;
  if (start === undefined || now < start) return false; // not started
  return end === undefined || now <= end; // not ended
}
export function generateShiftTotals(shiftLists?: Shift[]) {
  let totalSpots = 0;
  let totalShifts = 0;
  (shiftLists ?? []).forEach((shift) => {
    if (!shift.maxSignup) {
      totalSpots = Infinity;
      totalShifts++;
    } else if (shift.maxSignup && shift.signupsRemaining > 0) {
      totalSpots = totalSpots === Infinity ? Infinity : totalSpots + shift.signupsRemaining;
      totalShifts++;
    }
  });
  return {
    totalSpots,
    totalShifts,
  };
}

export function isEventOwner(event: Event, users: LoginData) {
  return event.ownerId === users.employeeId || event.ownerEmail?.includes(users.userId);
}

export function isEventAmbassador(event: Event, users: LoginData) {
  // event.ambassadors entries are stored as `alias-<alias>-employeeId-<employeeId>` (see the backend
  // shift.ts extractEmployeeIdsFromAmbassadors and the eventInfo coordinator display), so a bare
  // includes(employeeId) never matches. Parse each entry and match on the employeeId or the alias.
  // Guard malformed entries (and a not-yet-resolved login whose ids are '') so empty segments can't
  // produce a '' === '' false-positive match — mirroring the backend's parts.length >= 4 + non-empty filter.
  return event.ambassadors?.some((ambassador) => {
    const parts = ambassador.split('-');
    if (parts.length < 4) {
      return false;
    }
    const [, alias, , employeeId] = parts;
    return (!!employeeId && employeeId === users.employeeId) || (!!alias && alias === users.userId);
  });
}

export function canManageGivehubEvent(event: Event, users: LoginData): boolean {
  return Boolean(
    event.eventId &&
      event.source === Source.GIVEHUB &&
      (isEventOwner(event, users) ||
        isEventAmbassador(event, users) ||
        event.additionalCoordinators?.includes(users.userId))
  );
}

export function canManageVCEvent(event: Event, users: LoginData) {
  return (
    event.eventId &&
    event.source === Source.VC &&
    (isEventOwner(event, users) ||
      isEventAmbassador(event, users) ||
      event.additionalCoordinators?.includes(users.userId))
  );
}
